import { Observable, of, throwError } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import * as queryString from 'x-query-string';

import { ajaxGetJSON, ajaxPost } from '../utils';

export interface ServerResponse<T> extends ServerError {
  status: string;
  code?: number;
  data?: T | any;
}

export interface ServerError {
  message: string | string[];
}

const fetchParams: HeadersInit = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

export interface FetcherApi {
  get<T>(
    path: string,
    params: object | null,
    entityName?: string,
    headers?: HeadersInit,
  ): Observable<T>;

  post<T>(
    path: string,
    params: object | File,
    entityName?: string,
    headers?: HeadersInit,
  ): Observable<T>;

  put<T>(
    path: string,
    params: object,
    entityName?: string,
    headers?: HeadersInit,
  ): Observable<T>;
}

const setHeaders = (headers: HeadersInit) => {
  let secureHeaders = { ...(headers || {}) };
  secureHeaders = { ...secureHeaders };
  return secureHeaders;
};

export const Fetcher: FetcherApi = {
  get: <T>(
    path: string,
    params: object | null,
    entityName?: string,
    headers?: HeadersInit,
  ): Observable<T> => {
    const url = params ? `${path}?${queryString.encode(params)}` : path;
    const httpHeaders = {
      ...fetchParams,
      ...headers,
      ...{ 'X-Requested-With': 'XMLHttpRequest' },
    };
    return ajaxGetJSON(url, setHeaders(httpHeaders)).pipe(
      filter((res) => !!res),
      map((res: ServerResponse<T>) => toData(res)),
      switchMap((res: ServerResponse<T>) =>
        ['error', 'fail'].includes(res.status) ? throwError(res) : of(res),
      ),
      map((res: ServerResponse<T>) =>
        entityName ? res.data[entityName] : res.data ? res.data : res,
      ),
    );
  },
  post: <T>(
    path: string,
    params: object | File,
    entityName?: string,
    headers?: HeadersInit,
  ): Observable<T> => {
    const httpHeaders = Object.assign(headers || fetchParams, {
      'X-Requested-With': 'XMLHttpRequest',
    });
    const isFileBody = params instanceof File;

    let body = isFileBody ? params : { ...params };

    if (
      !!headers &&
      'Content-Type' in headers &&
      'application/x-www-form-urlencoded' === headers['Content-Type'] &&
      !isFileBody
    ) {
      body = queryString.encode(params);
    }

    return ajaxPost(path, body, setHeaders(httpHeaders)).pipe(
      map((res) => res.response),
      map((res: ServerResponse<T>) => toData(res)),
      switchMap((res: ServerResponse<T>) =>
        ['error', 'fail'].includes(res.status) ? throwError(res) : of(res),
      ),
      map((res: ServerResponse<T>) =>
        entityName ? res.data[entityName] : res.data ? res.data : res,
      ),
    );
  },
  put: <T>(
    path: string,
    params: object,
    entityName?: string,
    headers?: HeadersInit,
  ): Observable<T> => {
    const httpHeaders = Object.assign(headers || fetchParams, {
      'X-Requested-With': 'XMLHttpRequest',
    });
    let body = { ...params };

    if (
      !!headers &&
      'Content-Type' in headers &&
      'application/x-www-form-urlencoded' === headers['Content-Type']
    ) {
      body = queryString.encode(params);
    }

    return ajaxPost(path, body, setHeaders(httpHeaders), 'PUT').pipe(
      map((res) => res.response),
      map((res: ServerResponse<T>) => toData(res)),
      switchMap((res: ServerResponse<T>) =>
        ['error', 'fail'].includes(res.status) ? throwError(res) : of(res),
      ),
      map((res: ServerResponse<T>) =>
        entityName ? res.data[entityName] : res.data ? res.data : res,
      ),
    );
  },
};

function toData(res: any) {
  if (('message' in res || 'messages' in res) && 'data' in res) {
    return {
      ...res,
      data: {
        data: res.data,
        messages: res.message ? res.message : res.messages,
      },
    };
  }

  if ('data' in res) {
    return res;
  }

  return { data: res, status: res.status };
}
